---
title: "PDF form fields disappear after saving: what to check"
description: "Why a completed PDF form can look blank to its recipient, and when to download a flattened copy with fixed AcroForm field values."
locale: "en"
date: "2026-08-03"
tags: ["pdf", "forms", "acroform", "flatten"]
relatedTools: ["fill-pdf-form", "flatten-pdf"]
translations:
  ru: "propali-zapolnennye-polya-pdf"
faq:
  - q: "How is saving a form different from flattening it?"
    a: "A regular saved copy keeps interactive fields so they can be edited later. A flattened copy draws the visible AcroForm field appearances into the page content and removes the interactive fields."
  - q: "Can I correct a value in a flattened PDF?"
    a: "Not through the original form field. Keep the source or a regular filled copy if the answers may still change."
  - q: "Which forms does pdfy support?"
    a: "Standard AcroForms with text fields, checkboxes, radio buttons, dropdowns, and single or multi-select lists. XFA, scanned forms, and arbitrary field placement are not supported."
  - q: "Is the form uploaded to a server?"
    a: "No. Inspection, filling, and optional flattening run locally in the browser."
---

import { AcroFormFiller } from '@/features/acroform-filler'

You complete a 12-page application, save it, and email it. The recipient sees
the original blank form: no surname, no address, no checked boxes. Start with
an unglamorous test. Open the downloaded file, not the tab where you filled it,
then open that same copy in a second PDF viewer.

If the answers appear in only one viewer, the problem is often the form layer.
A field value and its visible appearance can be stored separately. One viewer
draws the value, another displays an empty box, and a print workflow may keep
only the page background.

For a standard AcroForm, a flattened copy is the practical handoff format. It
fixes visible values onto the pages and removes the editable controls. In the
form below, enable "Flatten fields after filling" before downloading. Keep the
source separately if any answer may still need a correction.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <AcroFormFiller />
</div>

## Identify the kind of PDF first

Can you click a field and get a text cursor? It is probably an interactive
form. The [PDF form filler](/zapolnit-pdf-formu) supports standard AcroForms:
text fields, checkboxes, radio buttons, dropdowns, and single or multi-select
lists. One file can contain up to 32 MB, 200 pages, and 500 fields.

If it is only a scanned image and clicking reveals no interactive fields, the
filler has nothing to edit. It does not guess where a name or signature belongs
and does not run OCR. A `Please wait...` page often points to XFA, an older
separate form technology.
XFA does not become an AcroForm through a checkbox either.

There is a third case: the fields exist, but the author disabled form filling
or encrypted the file. Do not try to work around the restriction. Ask the
author for an unrestricted copy or for the version intended for completion.

## What flattening changes

In pdfy, flattening applies to AcroForm fields. It does not intentionally turn
the entire page into an image, but text boxes, checkboxes, and selected options
stop being editable controls. That makes the copy easier to hand to an upload
portal, printer, or unknown PDF viewer.

Flattening is not a password, a qualified electronic signature, or protection
against every possible edit. Comments, highlights, stamps, handwriting, and
XFA-only elements are outside this mode. If another application already saved
the answers, use [flatten PDF](/flatten-pdf). If you are starting from the
original form, the switch in the embedded tool fills and flattens in one pass.

Processing stays in the browser. Still, open the finished `-flattened.pdf`
before sending it. Check two long fields, every checkbox, and the last page.
Keep the editable original until the recipient confirms the file. Re-entering
the form is annoying; discovering a fixed typo without the original is worse.

The [free PDF tools map](/blog/free-pdf-tools-without-sign-up) lists the other
pdfy jobs.
